/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  TextField, // editable message
  Grid
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import { safeFetch } from "./Safefetch";

import config from "../../config"; // keep import; maybe used elsewhere

// Local testing base URL
//const API_BASE = "http://127.0.0.1:8000";
const API_BASE = "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com"

// Small card wrapper
const SectionCard = ({ title, action, children }) => (
  <Box
    sx={{
      borderRadius: 2,
      p: 2.5,
      mb: 3,
      bgcolor: "#ffffff",
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1.5,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2933" }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Box>
);

const STATUS_LABEL = {
  draft: "Draft",
  queued: "Queued",
  sent: "Sent",
  cancelled: "Cancelled",
  error: "Error",
  document: "Document",
};

const STATUS_COLOR = {
  draft: "default",
  queued: "warning",
  sent: "success",
  cancelled: "error",
  error: "error",
  document: "default",
};

const NotificationsPane = () => {
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newScheduleAt, setNewScheduleAt] = useState("");
  const [creating, setCreating] = useState(false);


  // default to Draft filter
  const [filterStatus, setFilterStatus] = useState("draft");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // No JWT now
  const authHeaders = (extra = {}) => {
    const t = localStorage.getItem("labour_law_token");
    return {
      ...extra,
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // ----- ADMIN LIST -----
  const fetchNotifications = async () => {
    if (!API_BASE) return;
    setLoading(true);
    try {
      const url = `${API_BASE}/notifications/list/`;
      console.log("Fetching admin list:", url);
      const res = await safeFetch(url, {
        headers: {
          ...authHeaders,
        },
      });

      const text = await res.text().catch(() => "");
      let data = [];
      try {
        data = text ? JSON.parse(text) : [];
      } catch (parseErr) {
        console.error("Failed to parse admin list JSON:", parseErr, text);
        data = [];
      }

      if (!res.ok) {
        throw new Error(
          (data && data.error) || text || "Failed to load notifications"
        );
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Admin list fetch error:", err);
      showSnackbar(err.message || "Failed to load notifications", "error");
    } finally {
      setLoading(false);
    }
  };

  
  // ----- EDIT MESSAGE (BODY) -----
  const handleBodyChange = (id, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, body: value } : r))
    );
  };

  const createManualNotification = async () => {
  if (!newTitle.trim() || !newBody.trim()) {
    showSnackbar("Title and message are required", "error");
    return;
  }

  setCreating(true);
    try {
      const res = await safeFetch(
        `${API_BASE}/notifications/admin_create_notification/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("labour_law_token")}`,
          },
          body: JSON.stringify({
            title: newTitle,
            body: newBody,
            tag: "Announcement",
            schedule_at: newScheduleAt
              ? new Date(newScheduleAt).toISOString()
              : null,
            source_type: "manual",
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Failed to create notification");
      }

      showSnackbar("Notification saved successfully", "success");

      // reset form
      setNewTitle("");
      setNewBody("");
      setNewScheduleAt("");

      // refresh list
      await fetchNotifications();

    } catch (err) {
      showSnackbar(err.message || "Failed to create notification", "error");
    } finally {
      setCreating(false);
    }
  };

  // ----- SEND NOW -----
  const sendIdsNow = async (idsToSend) => {
    console.log("🔍 sendIdsNow idsToSend =", idsToSend);

    if (!idsToSend || !idsToSend.length) {
      showSnackbar("Select at least one notification to send", "error");
      return;
    }

    // Build overrides for selected rows using current edited body
    const overrides = {};
    rows.forEach((r) => {
      if (idsToSend.includes(r.id)) {
        overrides[r.id] = {
          body: r.body || "",
        };
      }
    });

    setSaving(true);
    try {
      const url = `${API_BASE}/notifications/send_now/`;
      console.log("Sending notifications:", url, idsToSend, overrides);
      const res = await safeFetch(url, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ids: idsToSend, overrides }),
      });

      const text = await res.text().catch(() => "");
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        console.error("Failed to parse send_now JSON:", parseErr, text);
        data = {};
      }

      if (!res.ok) {
        throw new Error(
          (data && data.error) || text || "Failed to send notification(s)"
        );
      }

      showSnackbar("Notification(s) sent to all users", "success");
      setSelectedIds([]);

      await fetchNotifications();
    } catch (err) {
      console.error("Send_now fetch error:", err);
      showSnackbar(err.message || "Failed to send notification(s)", "error");
    } finally {
      setSaving(false);
    }
  };

  const sendSelected = () => {
    sendIdsNow(selectedIds);
  };

  // ----- CANCEL -----
  const cancelNotification = async (id) => {
    setSaving(true);
    try {
      const url = `${API_BASE}/notifications/cancel/`;
      console.log("Cancelling notification:", url, id);
      const res = await safeFetch(url, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      const text = await res.text().catch(() => "");
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        console.error("Failed to parse cancel JSON:", parseErr, text);
        data = {};
      }

      if (!res.ok) {
        throw new Error(
          (data && data.error) || text || "Failed to cancel notification"
        );
      }

      showSnackbar("Notification cancelled", "success");
      await fetchNotifications();
    } catch (err) {
      console.error("Cancel fetch error:", err);
      showSnackbar(err.message || "Failed to cancel notification", "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    if (filterStatus === "all") return rows;
    return rows.filter((r) => r.status === filterStatus);
  }, [rows, filterStatus]);

  // Selection helpers → ALL rows are selectable
  const toggleAll = (checked) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    const selectable = filteredRows.map((r) => r.id);
    setSelectedIds(selectable);
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Box
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#f3f4f6",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827" }}>
          Notification Centre
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="queued">Queued</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="document">Document</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchNotifications}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>

          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={sendSelected}
            disabled={saving || selectedIds.length === 0}
            sx={{ textTransform: "none" }}
          >
            Send Selected
          </Button>
        </Box>
      </Box>
      <SectionCard title="Create Notification">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Title"
              fullWidth
              size="small"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Message"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Schedule At"
              type="datetime-local"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newScheduleAt}
              onChange={(e) => setNewScheduleAt(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              onClick={createManualNotification}
              disabled={creating}
              startIcon={<SendIcon />}
              sx={{ textTransform: "none" }}
            >
              {creating ? "Saving..." : "Save Notification"}
            </Button>
          </Grid>
        </Grid>
      </SectionCard>

      {/* ADMIN: Queue & history */}
      <SectionCard
        title="Notifications (saved in database + documents)"
        action={
          <Typography variant="body2" color="text.secondary">
            Showing {filteredRows.length} / {rows.length}
          </Typography>
        }
      >
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 280px)",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#d1d5db",
              borderRadius: 3,
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filteredRows.length
                    }
                    checked={
                      filteredRows.length > 0 &&
                      selectedIds.length > 0 &&
                      selectedIds.length === filteredRows.length
                    }
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row) => {
                const status = row.status || "draft";
                const checked = selectedIds.includes(row.id);

                return (
                  <TableRow key={row.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleOne(row.id)}
                      />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.title}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 260 }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={row.body || ""}
                        onChange={(e) =>
                          handleBodyChange(row.id, e.target.value)
                        }
                        InputProps={{
                          style: { fontSize: 13 },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={row.tag || "Update"}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">All users</Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[status] || status}
                        color={STATUS_COLOR[status] || "default"}
                        variant={
                          status === "draft" || status === "queued"
                            ? "outlined"
                            : "filled"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      {row.source_type && row.source_id ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.source_type} #{row.source_id}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          –
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {row.schedule_at
                        ? new Date(row.schedule_at).toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {row.sent_at
                        ? new Date(row.sent_at).toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="Send now to all mobile users">
                        <span>
                          <IconButton
                            size="small"
                            disabled={saving}
                            onClick={() => sendIdsNow([row.id])}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Cancel / ignore this notification">
                        <span>
                          <IconButton
                            size="small"
                            disabled={saving}
                            onClick={() => cancelNotification(row.id)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading notifications…
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No notifications found in the database
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPane;
